import React from "react";
import Select from "react-select";
import PropTypes from "prop-types";

const shapes = {
  round: "rounded-[12px]",
};

const variants = {
  fill: {
    white: "bg-white text-gray-900",
  },
};

const sizes = {
  sm: "h-[66px] px-6 text-[18px]",
  xs: "h-[62px] px-5 text-[16px]",
};

const SelectBox = React.forwardRef(
  (
    {
      children,
      className = "",
      name = "",
      options = [],
      isSearchable = false,
      isMulti = false,
      indicator,
      shape,
      variant = "fill",
      size = "xs",
      color = "white",
      onChange, 
      value,  // Pass value here for controlled component
      ...restProps
    },
    ref,
  ) => {
    const handleChange = (selectedOption) => {
      if (onChange) {
        onChange({
          target: {
            name,
            value: isMulti ? selectedOption : selectedOption?.value, // Ensure correct format for multi-select
          },
        });
      }
    };

    return (
      <>
        <Select
          ref={ref}
          options={options}
          value={isMulti ? value : value ? options.find(option => option.value === value) : null}  // Handle value for multi-select
          className={`${className} flex ${shape && shapes[shape]} ${size && sizes[size]} ${variant && variants[variant]?.[color]} w-full`}
          name={name}
          isSearchable={isSearchable}
          isMulti={isMulti}
          onChange={handleChange}  
          components={{
            IndicatorSeparator: () => null,
            ...(indicator && { DropdownIndicator: () => indicator }),
          }}
          styles={{
            indicatorsContainer: (provided) => ({
              ...provided,
              padding: undefined,
              flexShrink: undefined,
              width: "max-content",
              "& > div": { padding: 0 },
            }),
            container: (provided) => ({
              ...provided,
              zIndex: 0,
              alignItems: "center",
              width: "100%", // Ensure the container is full width
            }),
            control: (provided) => ({
              ...provided,
              backgroundColor: "transparent",
              border: "0 !important",
              boxShadow: "none !important",
              minHeight: "auto",
              width: "100%", // Make sure the select box is full width
              flexWrap: undefined,
              "&:hover": {
                border: "0 !important",
              },
            }),
            input: (provided) => ({
              ...provided,
              color: "inherit",
            }),
            option: (provided, state) => ({
              ...provided,
              display: "flex",
              minWidth: "max-content",
              width: "100%",
              backgroundColor: state.isSelected ? "#3b82f6" : "transparent",
              color: state.isSelected ? "#f9fafb" : "inherit",
              "&:hover": {
                backgroundColor: "#3b82f6",
                color: "#f9fafb",
              },
            }),
            singleValue: (provided) => ({
              ...provided,
              display: "flex",
              marginLeft: undefined,
              marginRight: undefined,
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: 0,
              display: "flex",
              flexWrap: undefined,
            }),
            placeholder: (provided) => ({
              ...provided,
              margin: 0,
            }),
            menuPortal: (base) => ({ ...base, zIndex: 999999 }),
            menu: (base) => ({
              ...base,
              minWidth: "100%", // Ensure the dropdown menu matches the select box width
              width: "100%",
            }),
          }}
          menuPortalTarget={document.body}
          closeMenuOnScroll={(event) => event.target.id === "scrollContainer"}
          {...restProps}
        />
        {children}
      </>
    );
  },
);


SelectBox.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,  // Ensure name is required for the SelectBox
  options: PropTypes.array,
  isSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.string,
  indicator: PropTypes.node,
  shape: PropTypes.oneOf(["round"]),
  size: PropTypes.oneOf(["sm", "xs"]),
  variant: PropTypes.oneOf(["fill"]),
  color: PropTypes.oneOf(["white"]),
};

export { SelectBox };
